import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
  TextField,
  Box,
  InputAdornment,
} from "@mui/material";
import {ArrowForwardIosSharp} from "@mui/icons-material";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {date, object, TypeOf, z} from "zod";
import {TimePicker} from "@mui/x-date-pickers";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";
import FormControl from "@/pages/auth/Login/components/SignUp";
import {zodResolver} from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import SnackbarUtil from "@/utils/SnackbarUtil";
import {useSnackbar} from "notistack";
import Util from "@/utils/Util";


export type BreakTimeSettingFields = {
  break_time_flag: boolean,
  break_start_time: any;
  break_end_time: any;
  pairing_rule: string;
  break_time_duration: number
}


type BreakTimeSettingProps = {
    break_time: BreakTimeSettingFields;
    view_data: any;
    schedule_used?: any;
    timesheetData?: any;
    pattern?: any;
  }

const BreakTimeSetting = (props: BreakTimeSettingProps, ref: any) => {
  const { t } = useTranslation();
  const defaultBreakTime = props.break_time;
  const param_id = props.view_data;
  const schedule_used = props.schedule_used;
  const pattern = props.pattern;
  const patternSchema = object({
    break_start_time: z.date(
          {
            errorMap: (issue, { defaultError }) =>
              ({ message: issue.code === "invalid_date" ? t("common.Invalid time format.") : issue.code === "invalid_type" ? t("common.This field is required!") : defaultError })
          }),
    break_end_time: z.date(
          {
            errorMap: (issue, { defaultError }) =>
              ({ message: issue.code === "invalid_date" ? t("common.Invalid time format.") : issue.code === "invalid_type" ? t("common.This field is required!") : defaultError })
          }),
    pairing_rule: z.string(),
    break_time_duration: z.number().min(1, t("common.Value must be between 1 and 180")).max(180, t("common.Value must be between 1 and 180")),
    break_time_flag: z.boolean()
  });

  type PatternInput = TypeOf<typeof patternSchema>;


  const BreakTimeSettingForm = useForm<PatternInput>({
    defaultValues: {
      break_time_flag: defaultBreakTime?.break_time_flag ?? false,
      break_start_time: defaultBreakTime?.break_start_time ?? new Date('1990-01-01T12:00:00'),
      break_end_time: defaultBreakTime?.break_end_time ?? new Date('1990-01-01T13:00:00'),
      pairing_rule: defaultBreakTime?.pairing_rule ?? "punch_required",
      break_time_duration: 30
    },
    resolver: zodResolver(patternSchema)
  })
  const {
    register,
    trigger,
    setError,
    formState: { errors },
  } = BreakTimeSettingForm;
  const { watch } = BreakTimeSettingForm;
  const pairingRule = watch("pairing_rule", "punch_required");
  
  // When pattern changes to "working", automatically set pairing_rule to "fixed_duration"
  // Don't change pairing_rule when pattern is not "working" (preserve user's selection)
  useEffect(() => {
    if (pattern === "working") {
      BreakTimeSettingForm.setValue("pairing_rule", "fixed_duration");
    }
  }, [pattern]);
  
  // When timesheetData loads, set the values from API
  useEffect(() => {
    if (props.timesheetData) {
      const break_pairs = props.timesheetData?.pattern.break_pairs;
      const pairs = Array.isArray(break_pairs) ? break_pairs[0] : break_pairs;
      if (pairs && pairs.start && pairs.end) {
        const baseDate = new Date('1990-01-01T00:00:00');
        const setStartTime = Util.time2datetime(pairs.start);
        const setEndTime = Util.time2datetime(pairs.end);
        BreakTimeSettingForm.setValue("pairing_rule", pairs.pairing_rule || "punch_required");
        BreakTimeSettingForm.setValue("break_start_time", setStartTime);
        BreakTimeSettingForm.setValue("break_end_time", setEndTime);
        // Use API value if exists, otherwise use default 30
        BreakTimeSettingForm.setValue("break_time_duration", pairs.break_time_duration ?? 30);
      }
    }
  }, [props.timesheetData]);
  useImperativeHandle(ref, () => ({
    breakTime:async () => {
      const isValid = await trigger();
      return BreakTimeSettingForm.getValues()
    }
  }));
  return (
    <Accordion disableGutters={true} sx={{
      boxShadow: "none",
      border: "none",
      width: "100%",
    }}>
        <FormProvider {...BreakTimeSettingForm}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={6}
          component="form"
          sx={{
            width: '100%',
            pl: 0, // 清除左侧外边距
            mt: 2
          }}
        >
          <Grid item xs={12} sx={{ pl: 0 }}>
            <NGInputLabel required label={t("att.timeSheet.Break Time Mode")}/>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', width: '21%', ml: 0, pl: 0 }}>
              <Controller
                control={BreakTimeSettingForm.control}
                name="pairing_rule"
                render={({ field }) => {
                  const formValue = BreakTimeSettingForm.getValues("pairing_rule");
                  const currentValue = formValue != null ? formValue : "punch_required";
                  return(
                  <Select
                    {...field}
                    size="medium"
                    fullWidth
                    disabled={schedule_used !== 0}
                    value={currentValue}
                    sx={{ flex: 1 }}
                    onChange={(e) => {
                      const value = e.target.value.toString();
                      field.onChange(e.target.value);
                    }}
                  >
                    {/*<MenuItem value="punch_required">{t("att.timeSheet.Punch Required")}</MenuItem>*/}
                    {/*<MenuItem value="auto_deducted">{t("att.timeSheet.Auto Deducted")}</MenuItem>*/}
                    {pattern !== "working" && <MenuItem value="punch_required">{t("att.timeSheet.Clock In Out")}</MenuItem>}
                    <MenuItem value="fixed_duration">{t("att.timeSheet.Fixed Duration")}</MenuItem>
                  </Select>
                );
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sx={{p: "0 16px", pl: 0}}>
            {pairingRule == "punch_required" ?
              <Grid container columnSpacing={2} sx={{width: '44%', pl: 0}}>
                <Grid item xs={3} >
                  <NGInputLabel required label={t("att.timeSheet.Break Start Time")}/>
                  <Grid sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 1, pl: 0}}>
                    <Controller
                      control={BreakTimeSettingForm.control}
                      name={"break_start_time"}
                      render={({field, fieldState}) =>
                        <TimePicker
                          value={field.value || null}
                          ampm={false}
                          onChange={(newValue: any) => {
                            if (newValue >= BreakTimeSettingForm.getValues("break_end_time")) {
                              SnackbarUtil.error(t("export.Break start time cannot greater than the end time"), {
                                anchorOrigin: {
                                  vertical: 'top',
                                  horizontal: 'center'
                                }
                              })
                              return;
                            }
                            field.onChange(newValue)
                          }}
                          disabled={schedule_used != 0}
                          renderInput={(fieldProps) => {
                            return <TextField
                              size={"medium"}
                              fullWidth={true}
                              {...fieldProps}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message || ""}
                            />
                          }}
                        />
                      }
                    />
                  </Grid>
                </Grid>
                <Grid item xs={3} >
                  <NGInputLabel required label={t("att.timeSheet.Break End Time")}/>
                  <Grid sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 1, pl: 0}}>
                    <Controller
                      control={BreakTimeSettingForm.control}
                      name={"break_end_time"}
                      render={({field, fieldState}) =>
                        <TimePicker
                          value={field.value || null}
                          ampm={false}
                          onChange={(newValue: any) => {
                            if (newValue <= BreakTimeSettingForm.getValues("break_start_time")) {
                              SnackbarUtil.error(t("export.Break start time cannot greater than the end time"), {
                                anchorOrigin: {
                                  vertical: 'top',
                                  horizontal: 'center'
                                }
                              })
                              return;
                            }
                            field.onChange(newValue)
                          }}
                          disabled={schedule_used != 0}
                          renderInput={(fieldProps) => {
                            return <TextField
                              size={"medium"}
                              fullWidth={true}
                              {...fieldProps}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message || ""}
                            />
                          }}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              : <Grid container xs={12} sx={{width: '43%', pl: 0}}>
                    <Grid item xs={6} sx={{padding: '8px', pl: 0}}>
                       <NGInputLabel required label={t("att.timeSheet.Break Time Duration")}/>
                       <Grid sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 1, pl: 0}}>
                          <TextField
                            type="number"
                            size="medium"
                            disabled={schedule_used != 0}
                            fullWidth
                            error={!!errors['break_time_duration']}
                            helperText={errors['break_time_duration']?.message || ""}
                            inputProps={{ min: 1, max: 180 }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">{t("att.timeSheet.Break Time Duration min")}</InputAdornment>,
                            }}
                            {...register("break_time_duration", { valueAsNumber: true })}
                            onChange={(e) => {
                              let value = Number(e.target.value)
                              if (value > 180) {
                                value = 180
                              } else if (value < 1) {
                                value = 1
                              }
                              BreakTimeSettingForm.setValue("break_time_duration", value)
                            }}
                          />
                    </Grid>
                  </Grid>
              </Grid>}
          </Grid>
        </Grid>
        </FormProvider>
    </Accordion>
  )
};
export default forwardRef(BreakTimeSetting)
